import { styled } from '@mui/material';

import {
  DocumentIcon,
  EditDocumentIcon,
  RefreshIcon,
} from '../../../icons';
import { Button } from '../../atoms/Button';
import { Chip } from '../../atoms/Chip';
import { Link } from '../../atoms/Link';
import { Stack } from '../../atoms/Stack';
import { Tag } from '../../atoms/Tag';
import { Typography } from '../../atoms/Typography';
import { Card } from '../Card';
import { CardActions } from '../CardActions';
import { CardContent } from '../CardContent';
import { HTMLDisplay } from '../HTMLDisplay';
import { ResourceMenuActions } from '../../organisms/ResourceMenuActions';
import {
  type QuoteCardAction,
  type QuoteCardProps,
  type QuoteDocument,
  type QuoteItem,
  type QuoteStatus,
  useQuoteCard,
} from './useQuoteCard';

type Props = QuoteCardProps;

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}));

const HeaderMeta = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
  flex: 1,
}));

const HeaderActions = styled('div')({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
});

const Separator = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  userSelect: 'none',
}));

export function QuoteCard(props: Props) {
  const hook = useQuoteCard(props);

  return (
    <Card data-testid={props['data-testid']}>
      <CardContent>
        <Stack spacing={1}>
          <HeaderContainer>
            <HeaderMeta>
              {props.providerHref || props.onProviderClick ? (
                <Link
                  href={props.providerHref}
                  onClick={props.onProviderClick}
                  variant="subtitle1"
                  underline="hover"
                >
                  {hook.providerName}
                </Link>
              ) : (
                <Typography variant="subtitle1">
                  {hook.providerName}
                </Typography>
              )}

              <Separator aria-hidden="true">-</Separator>

              {hook.formattedAmount ? (
                <Typography variant="body1Emphasis">
                  {hook.formattedAmount}
                </Typography>
              ) : (
                <Typography variant="body2">
                  Montant non renseigné
                </Typography>
              )}

              {hook.normalizedStatus === 'accepted' ? (
                <Tag
                  color="done"
                  label={hook.formattedDateApproved ? `BPA donné le ${hook.formattedDateApproved}` : 'BPA donné'}
                  variantIcon='state'
                />
              ) : hook.normalizedStatus === 'refused' ? (
                <Tag
                  color="cancelled"
                  label="Refusé"
                  variantIcon='state'
                />
              ) : (
                <Tag
                  color="pending"
                  label="En attente"
                  variantIcon='state'
                />
              )}
            </HeaderMeta>

            <HeaderActions>
              {props.action ? (
                props.action
              ) : hook.hasActions ? (
                <ResourceMenuActions
                  size="small"
                  actions={hook.actions}
                  tooltip={hook.actionsButtonTooltip}
                  ariaLabel={hook.actionsButtonAriaLabel}
                />
              ) : null}
            </HeaderActions>
          </HeaderContainer>

          {props.quote.title ? (
            <Typography variant="body2Emphasis">
              {props.quote.title}
            </Typography>
          ) : null}

          {hook.hasComment ? (
            <HTMLDisplay content={props.quote.comment!} maxLines={props.maxCommentLines ?? 3} />
          ) : null}

          {hook.hasDocuments ? (
            <Stack direction="row" spacing={1} alignItems="center" wrap="wrap">
              {hook.quoteDocuments.map((doc, index) => {
                const docUrl = doc.url || doc.file_url;
                if (docUrl) {
                  return (
                    <Chip
                      key={doc.id ?? index}
                      icon={<DocumentIcon fontSize="small" />}
                      label={doc.name || 'Devis'}
                      href={docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="outlined"
                    />
                  );
                }

                return (
                  <Tag
                    key={doc.id ?? index}
                    color="default"
                    label={doc.name || 'Devis'}
                    icon={<DocumentIcon fontSize="small" />}
                  />
                );
              })}

              {hook.bpaUrl ? (
                <Chip
                  icon={<DocumentIcon fontSize="small" />}
                  label={hook.bpaName}
                  href={hook.bpaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ) : hook.bpaDoc ? (
                <Tag
                  color="done"
                  label={hook.bpaName}
                  icon={<DocumentIcon fontSize="small" />}
                />
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </CardContent>

      {hook.hasFooterActions ? (
        <CardActions>
          {props.footerActions ? (
            props.footerActions
          ) : hook.normalizedStatus === 'pending' ? (
            <>
              {props.onRefuse ? (
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={hook.handleRefuse}
                >
                  Refuser
                </Button>
              ) : null}
              {props.onAccept ? (
                <Button
                  size="small"
                  color="success"
                  variant="contained"
                  onClick={hook.handleAccept}
                >
                  Accepter
                </Button>
              ) : null}
            </>
          ) : hook.normalizedStatus === 'accepted' ? (
            <>
              {props.onResetToPending ? (
                <Button
                  size="small"
                  variant="text"
                  color='default'
                  onClick={hook.handleResetToPending}
                  startIcon={<RefreshIcon fontSize="small" />}
                >
                  Remettre en attente
                </Button>
              ) : null}
              {hook.showAttachBpaButton ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<EditDocumentIcon fontSize="small" />}
                  onClick={hook.handleAttachBpa}
                >
                  Joindre le bon pour accord
                </Button>
              ) : null}
            </>
          ) : (
            props.onResetToPending ? (
              <Button
                size="small"
                variant="text"
                color='default'
                onClick={hook.handleResetToPending}
                startIcon={<RefreshIcon fontSize="small" />}
              >
                Remettre en attente
              </Button>
            ) : null
          )}
        </CardActions>
      ) : null}
    </Card>
  );
}

export type { QuoteCardAction, QuoteCardProps, QuoteDocument, QuoteItem, QuoteStatus };
